import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch } from 'react-redux';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

import { loginSchema } from '../../helpers/validations/loginValidation';
import { loginSuccess, loginFailure, loginRequest } from '../../features/auth/authSlice';
import { AdminLogin, EmployeeLogin, ParentLogin, StudentLogin } from '../../api/login/loginService';
import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../hooks/useAuth';
import { handleLogout } from '../../services/logoutHandler';
import Cookies from 'js-cookie';
import { useSnackbar } from '../../components/MUIComponent/FeedbackComponent/SnackBar/SnackbarContext';
import Button from '../../components/Button/Button';
import AnnouncementsLoginCard from './AnnouncementsLoginCard';
import EventLoginCard from './EventLoginCard';
import { useSelector } from 'react-redux';
import '../../styles/layout/_mainLayout.scss';
import { loginKidSuccess } from '../../features/auth/kidAuthSlice';
import { Roles } from '../../constants/params/constants';
import { useParentKidAuth } from '../../hooks/parentKidUseAuth';
import { StudentVisitByParent } from '../../api/StudentService/StudentServide';
import { decryptPasswordAES, encryptPasswordAES } from '../../services/EncriptPasswordAES';
import FeatureSlider from './FeatureSlider';
import { setAcademicYearList } from '../../features/academicYear/academicYearListSlice';
import { GetAllAcademicYearList } from '../../api/SchoolService/SchoolService';
import { setAcademicYear } from '../../features/academicYear/academicYearSlice';
import { getSchoolProfileSuccess } from '../../features/schoolProfile/schoolProfileSlice';
import configAPI from '../../api/config';
import { setSelectedSchool } from '../../features/schoolProfile/selectedSchoolSlice';
import { setStudentAcadYear } from '../../features/academicYear/addStudentAcadYearSlice';
import LoopComponent from './LoopComponent';
import PeriodicPasswordReset from './PeriodicPasswordReset';
const roles = [
  { to: ROUTES.ADMINSIGNIN,    label: "Admin",    icon: "fa-user-shield",    color: "#06B6D4" },
  { to: ROUTES.STUDENTSIGNIN,  label: "Student",  icon: "fa-graduation-cap", color: "#2f6bff" },
  { to: ROUTES.EMPLOYEESIGNIN, label: "Employee", icon: "fa-briefcase",      color: "#16a34a" },
  { to: ROUTES.PARENTSIGNIN,   label: "Parent",   icon: "fa-users",          color: "#f97316" },
];

const LoginPage = ({ setStep, setFormData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useAuth();
  const { parentkidUser } = useParentKidAuth();
  const { schoolProfileData } = useSelector(state => state.schoolProfile);
  const [academicYearList, setAcademicYearDataList] = useState([]);

  const User = parentkidUser?.roleName === Roles[3].RoleName ? parentkidUser : user;
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [position, setPosition] = React.useState(50);
  const location = useLocation();
  const { PARENTSIGNIN, STUDENTSIGNIN, EMPLOYEESIGNIN } = ROUTES;
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [passwordResetUserName, setPasswordResetUserName] = useState('');


  const [openLicenseModel, setOpenLicenseModel] = React.useState(false);
  const handleOpenLicenseModel = () => setOpenLicenseModel(true);
  const handleCloseLicenseModel = () => setOpenLicenseModel(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm({
    resolver: yupResolver(loginSchema),
  });
  const handleTogglePassword = () => setShowPassword(!showPassword);

  const getFullImageUrl = path => {
    if (!path) return null;
    if (path.startsWith('http') || path.startsWith('https')) return path;
    return `${configAPI.REACT_APP_IMAGE_DYNAMIC_URL}${path}`;
  };

  const fetchAcademicYearList = async (schoolID) => {
    try {
      if (isLoading) {
        return;
      }
      setIsLoading(true);
      const formData = new FormData();
      formData.append("SchoolID", schoolID)
      const res = await GetAllAcademicYearList(formData);
      if (res.success) {
        const list = res?.data.length > 0 ? res?.data.filter(acd => acd.status === "Active") : [];
        dispatch(setAcademicYearList(list));

        const toLocalDateString = (date) => {
          const d = new Date(date);
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}`;
        };
        const todayStr = toLocalDateString(new Date());
        let current = list.find(session => {
          const startStr = toLocalDateString(session.startDate);
          const endStr = toLocalDateString(session.endDate);

          return todayStr >= startStr && todayStr <= endStr;
        });

        if (!current && list.length > 0) {
          current = [...list].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )[0];
        }

        const schoolProfileUpdate = { ...schoolProfileData, academicYearEnd: current.startDate, academicYearStart: current.endDate };
        dispatch(getSchoolProfileSuccess(schoolProfileUpdate));
        dispatch(setAcademicYear(current));
        dispatch(setStudentAcadYear(current));
      } else {
        setAcademicYearDataList([]);
      }
    } catch (error) {
      setAcademicYearDataList([]);
      // showSnackbar('Failed to fetch academic year list', 'warning');
    } finally {
      setIsLoading(false);
    }
  };


  const onSubmit = async data => {
    dispatch(loginRequest());
    try {
      const encryptedPassword = encryptPasswordAES(data.Password);
      const formData = {
        userName: data.UserName,
        password: encryptedPassword,
      };

      let response;
      const routeName = location.pathname;

      switch (routeName) {
        case STUDENTSIGNIN:
          response = await StudentLogin(formData);
          break;
        case PARENTSIGNIN:
          response = await ParentLogin(formData);
          break;
        case EMPLOYEESIGNIN:
          response = await EmployeeLogin(formData);
          break;
        default:
          response = await AdminLogin(formData);
          break;
      }
if (
  response?.message?.trim().toLowerCase() .replace(/\s+/g, '') === 'requirestwofactor') {
  setFormData({
    email: data.UserName,
    newPassword: '',
  });

  setStep(2);

  return;
}

      if (response?.data?.passwordResetRequired === true || response?.passwordResetRequired === true) {
        setPasswordResetUser(data.UserName);
        setPasswordResetUserName(
          response?.data?.userName || response?.userName || data.UserName
        );

        return;
      }

      if (response.success === true) {
        const responseData = response;
        localStorage.setItem("token", responseData?.data?.accessToken)
        localStorage.setItem("refreshToken", responseData?.data?.refreshToken)

        const documents = JSON.parse(responseData?.data?.documents || '[]');
        const profileDoc = documents.find(doc => doc.DocumentName === "Profile Image");
        const ProfileUrl = profileDoc?.DocumentImage || '';

        const isSchoolListRole =
          responseData?.data?.roleName === Roles[5].RoleName ||
          responseData?.data?.roleName === Roles[6].RoleName ||
          responseData?.data?.roleName === Roles[2].RoleName ||
          responseData?.data?.roleName === Roles[0].RoleName;

        const isSchoolListRoleSchoolList =
          responseData?.data?.roleName === Roles[5].RoleName ||
          responseData?.data?.roleName === Roles[6].RoleName ||
          responseData?.data?.roleName === Roles[0].RoleName;

        if (isSchoolListRole) {
          if (responseData?.data?.roleName === Roles[2].RoleName) {
            const res = await fetchAcademicYearList(responseData?.data?.schoolID);
          } else {
            const res1 = await fetchAcademicYearList(responseData?.data?.schoolID);
          }
        } else {
          const acadyear = responseData?.data?.academicYear || {};
          const schoolProfileUpdate = { ...schoolProfileData, academicYearEnd: acadyear?.startDate ?? null, academicYearStart: acadyear?.endDate ?? null };
          dispatch(getSchoolProfileSuccess(schoolProfileUpdate));
          dispatch(setAcademicYear(acadyear));
          dispatch(setStudentAcadYear(acadyear));
        }

        const payload = {
          classID: responseData?.data?.studentClassDetails?.[0]?.classID,
          sectionID: responseData?.data?.studentClassDetails?.[0]?.sectionID,
          streamID: responseData?.data?.studentClassDetails?.[0]?.streamID,
          subStreamID: responseData?.data?.studentClassDetails?.[0]?.subStreamID,
          studentID: responseData?.data?.studentID,
          employeeID: responseData?.data?.employeeID,
          adminID: responseData?.data?.adminID,
          parentID: responseData?.data?.parentID,
          firstName: responseData?.data?.firstName,
          fullName: responseData?.data?.fullName,
          lastName: responseData?.data?.lastName,
          email: responseData?.data?.email,
          userName: responseData?.data?.userName,
          authEmail: responseData?.data?.authEmail,
          isEnable: responseData?.data?.isEnable,
          parentOrGuardianEmailID: responseData?.data?.parentOrGuardianEmailID,
          phoneNumber: responseData?.data?.phoneNumber,
          roleID: responseData?.data?.roleID,
          roleName: responseData?.data?.roleName,
          schoolsRoutes: responseData?.data?.schoolsRoutes,
          routes: responseData?.data?.routes,
          groupID: responseData?.data?.groupID,
          schoolID: responseData?.data?.schoolID,
          schoolName: responseData?.data?.schoolName,
          schoolLicense: responseData?.data?.schoolLicense,
          isParentVisitStudentDash: responseData?.data?.isParentVisitStudentDash,
          ProfileUrl: responseData?.data?.profileImage || ProfileUrl,
          registrationNumber: responseData?.data?.registrationNumber,
          className: responseData?.data?.studentClassDetails?.[0]?.className,
          sectionName: responseData?.data?.studentClassDetails?.[0]?.sectionName,
          streamName: responseData?.data?.studentClassDetails?.[0]?.streamName,
          subStreamName: responseData?.data?.studentClassDetails?.[0]?.subStreamName,
          parentStudentList: responseData?.data?.parentStudentList,
          schoolList: responseData?.data?.schoolList,
          classTeacher: responseData?.data?.classTeacher || [],
          IsDefaultPassword: responseData?.data?.isDefaultPassword,
          houseId: responseData?.data?.houseId,
          houseName: responseData?.data?.houseName,
        };
        if (responseData?.data?.schoolList !== undefined && Array.isArray(responseData?.data?.schoolList)) {
          let selected = null;
          if (!selected) {
            selected =
              responseData?.data?.schoolList.find(sch => sch.schoolID === responseData?.data?.schoolID) || responseData?.data?.schoolList[0];
          }
          dispatch(setSelectedSchool(selected));
        } else {
          const fallbackOption = {
            schoolName: responseData?.data?.schoolName,
            schoolBranchName: responseData?.data?.schoolBranchName,
            schoolID: responseData?.data?.schoolID,
            schoolLogo: getFullImageUrl(responseData?.data?.schoolLogo),
          };
          dispatch(setSelectedSchool(fallbackOption));
        }

        if (responseData?.data?.roleName === Roles[3].RoleName && responseData?.data?.parentStudentList.length > 0) {
          dispatch(loginKidSuccess(payload))
        }
        else {
          dispatch(loginSuccess(payload))
        }
        if (responseData?.data?.roleName === Roles[3].RoleName && responseData?.data?.parentStudentList.length > 0) {
          try {
            setIsLoading(true);

            const payloadVisit = {
              ParentID: responseData?.data?.parentID,
              StudentID: responseData?.data?.parentStudentList[0].studentID
            }
            const res = await StudentVisitByParent(payloadVisit);
            if (res?.success) {
              const responseData = res;
              const documents = JSON.parse(responseData?.data?.documents || '[]');
              const profileDoc = documents.find(doc => doc.DocumentName === "Profile Image");
              const ProfileUrl = profileDoc?.DocumentImage || '';
              const kidPayload = {
                classID: responseData?.data?.studentClassDetails?.[0]?.classID,
                sectionID: responseData?.data?.studentClassDetails?.[0]?.sectionID,
                studentID: responseData?.data?.studentID,
                employeeID: responseData?.data?.employeeID,
                adminID: responseData?.data?.adminID,
                firstName: responseData?.data?.firstName,
                fullName: responseData?.data?.fullName,
                lastName: responseData?.data?.lastName,
                email: responseData?.data?.email,
                phoneNumber: responseData?.data?.phoneNumber,
                roleID: responseData?.data?.roleID,
                roleName: responseData?.data?.roleName,
                routes: responseData?.data?.routes,
                schoolID: responseData?.data?.schoolID,
                schoolName: responseData?.data?.schoolName,
                isParentVisitStudentDash: responseData?.data?.isParentVisitStudentDash,
                ProfileUrl: responseData?.data?.profileImage || ProfileUrl,
                registrationNumber: responseData?.data?.registrationNumber,
                className: responseData?.data?.studentClassDetails?.[0]?.className,
                sectionName: responseData?.data?.studentClassDetails?.[0]?.sectionName,
                streamName: responseData?.data?.studentClassDetails?.[0]?.streamName,
                subStreamName: responseData?.data?.studentClassDetails?.[0]?.subStreamName,
                parentStudentList: responseData?.data?.parentStudentList,
                houseId: responseData?.data?.houseId,
                houseName: responseData?.data?.houseName,
              };

              dispatch(loginSuccess(kidPayload));
            }
          } catch (error) {
            showSnackbar('Failed to load classes', "error");
          } finally {
            setIsLoading(false);
          }



        } else {
          dispatch(loginKidSuccess(null));
        }

        // if (responseData?.data?.schoolLicense == null){
        //   showSnackbar("School license is unavailable", "warning");
        //   navigate('/trial-ended')
        // } else if (responseData?.data?.schoolLicense?.remainingDays === 0){
        //   showSnackbar("School license have expired", "warning");
        //   navigate('/trial-ended')
        // } else {
        navigate('/');
        // }

        if (rememberMe) {
          // Cookies.set('auth', JSON.stringify(responseData), {
          //     expires: 7,
          //     secure: true,
          //     sameSite: 'Strict',
          // });
          const isSecure = window.location.protocol === 'https:';

          Cookies.set('savedUsername', data.UserName, {
            expires: 7,
            sameSite: 'Lax',
            secure: isSecure,
          });
          Cookies.set('savedPassword', data.Password, {
            expires: 7,
            sameSite: 'Lax',
            secure: isSecure,
          });
        } else {
          Cookies.remove('auth');
          Cookies.remove('savedUsername');
          Cookies.remove('savedPassword');
        }

        showSnackbar('Login successful!', 'success');
      } else {
        dispatch(loginFailure('Login failed'));
        showSnackbar('Invalid credentials', 'error');
      }
    } catch (error) {
      dispatch(loginFailure(error.message || 'Login failed'));
      // toast.error(error.message || 'Invalid credentials');
      showSnackbar(error.message || 'Invalid credentials', 'error');
    }
  };

  const handleNavigate = () => {
    const routeName = location.pathname;

    switch (routeName) {
      case ROUTES.ADMINSIGNIN:
        navigate(ROUTES.ADMINFORGOTPASSWORD);
        break;
      case ROUTES.PARENTSIGNIN:
        navigate(ROUTES.PARENTFORGOTPASSWORD);
        break;
      case ROUTES.EMPLOYEESIGNIN:
        navigate(ROUTES.EMPLOYEEFORGOTPASSWORD);
        break;
      default:
        navigate(ROUTES.FORGOTPASSWORD);
        break;
    }
  };
  const customColors = useSelector(state => state.theme.customColors);

  const onFormError = formErrors => {
    const firstError = Object.values(formErrors)[0];
    if (firstError?.message) {
      // toast.error(firstError.message);
      showSnackbar(firstError.message, 'error');
    }
  };

  useEffect(() => {
    handleLogout(dispatch, User, navigate, location);
  }, []);

  useEffect(() => {
    if (User) {
      localStorage.removeItem('lastRole');
    }
  }, [User]);

  useEffect(() => {
    const savedUsername = Cookies.get('savedUsername');
    const savedPassword = Cookies.get('savedPassword');

    if (savedUsername && savedPassword) {
      setValue('UserName', savedUsername);
      setValue('Password', savedPassword);
      setRememberMe(true);
    }
  }, [setValue]);
  
const linkStyle = ({ isActive }) => ({
  display: "flex",
  flexDirection: "column",     
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  padding: "12px 8px",        
  border: `1px solid ${isActive ? "#8f01ff" : "rgb(235 235 236)"}`,
  borderRadius: "8px",
  textDecoration: "none",
  color: isActive ? "#fff" : "rgb(80 80 80)",   
  fontWeight: 600,
  fontSize: "15px",
  background: isActive ? "#8f01ff" : "#fff",    
  // boxShadow: isActive
  //   ? "0 6px 18px rgba(143,1,255,.20)"
  //   : "0 1px 3px rgba(0,0,0,.04)",
  transition: "all .25s ease",
});
  return (
    <Box
      position={'relative'}
      sx={{
        // backgroundImage: 'linear-gradient(120deg,#edf7f3,#fff5e8)' 
        backgroundImage: 'url(/assets/images/studentdash/voilogin-bg.png)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        // backgroundPosition: 'center'
      }}
    >
      {/* <Box py={2} px={{ xs: 2, sm: 3, md: 4, lg: 5 }} position={'relative'} zIndex={1}>
        <Box
          component="img"
          src="/assets/images/logo/brandlogo.png"
          alt="Logo"
          sx={{
            width: 160,
            height: 'auto',
            zIndex: 10,
          }}
        />
      </Box> */}
      <Box
        display={'flex'}
        alignItems={'center'}
        // minHeight={`calc(100vh - 68px)`}
        minHeight={`calc(100vh)`}
        width={'100%'}
        position={'relative'}
        zIndex={1}
      >
        <Box
          display={'flex'}
          justifyContent={{ xs: 'center', sm: 'center', md: 'center', lg: 'center' }}
          px={{ xs: 0, sm: 3, md: 4, lg: 5 }}
          flexWrap={'wrap'}
          width={'100%'}
          gap={{ xs: 0, sm: 3, md: 3, lg: 3 }}
        >
          {/* <EventLoginCard /> */}
          <Box display={'flex'} bgcolor="#fff" borderRadius={2} width={{ xs: '90%', sm: 'initial', md: 'initial', lg: 'initial', xl: 'initial' }} overflow={'hidden'} >
            <Box
              className=" portal-card log-width"
              width={{ xs: '100%', sm: 500, md: 500, lg: 500, xl: 550 }}
            >
              <Box height={'100%'} borderRight={'solid 1px #e2e8f0'}
                borderBottom={0}>
                <Box
                  className="dashboard-card form_card"

                  pl={{ xs: 2, sm: 4, md: 4, lg: 4, xl: 6 }}
                  pr={{ xs: 2, sm: 4, md: 4, lg: 4, xl: 4 }}
                  // py={9}
                  pt={{ xs: 3, sm: 3, md: 3, lg: 3, xl: 6 }}
                  pb={{ xs: 3, sm: 3, md: 3, lg: 3, xl: 6 }}
                  // height={'100%'}
                  borderRadius={'0!important'}
                // bgcolor="#fff"
                >
                  <Box pb={{ xs: 2.5, sm: 2.5, md: 1, lg: 1, xl: 3 }} position={'relative'} zIndex={1}>
                    <Box
                      component="img"
                      src="/assets/images/logo/brandlogo.png"
                      alt="Logo"
                      width={{ xs: 160, sm: 160, md: 215, lg: 190, xl: 215 }}
                      sx={{
                        // width: 215,
                        height: 'auto',
                        zIndex: 10,
                      }}
                    />
                  </Box>

                  {passwordResetUser ? (
                    <PeriodicPasswordReset
                      userName={passwordResetUser}
                      registeredUserName={passwordResetUserName}
                      onCancel={() => {
                        setPasswordResetUser(null);
                        setPasswordResetUserName('');
                      }}
                    />
                  ) : (
                    <>
                  {/* Login Card */}
                  <Box mb={{ xs: 1.5, sm: 1.5, md: 1.5, lg: 1.5, xl: 2 }}>
                    <Typography
                      variant="h6"
                      mb={{ xs: 1, sm: 1, md: 2, lg: 1, xl: 2 }}
                      color="#1d293d"
                      className="card-title text-center"
                      fontSize={{ xs: 20, sm: 20, md: 26, lg: 22, xl: 26 }}
                    >
                      Let's get you signed in
                    </Typography>
                    <Typography variant="body2" color="rgba(0,0,0,0.6)">
                      Access your academic dashboard
                    </Typography>
                  </Box>
<Stack direction="row" sx={{ width: "100%" }} mb={{ xs: 1.5, lg: 1.5, xl: 2 }}>
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
      gap: 1.25,  
      width: "100%",
    }}
  >
{roles.map((r) => (
  <NavLink key={r.label} to={r.to} style={linkStyle}>
    {({ isActive }) => (
      <>
        <i
          className={`fa-solid ${r.icon}`}
          style={{
            fontSize: 26,
            color: isActive ? "#fff" : r.color, 
          }}
        />
        {r.label}
      </>
    )}
  </NavLink>
))}
  </Box>
</Stack>
                  <Box mt={2}>
                    <form onSubmit={handleSubmit(onSubmit, onFormError)} noValidate>
                      <Box
                        sx={{
                          mx: 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: { xs: 2, sm: 2, md: 2, lg: 1, xl: 2 },
                          height: '100%',
                        }}
                      >
                        <TextField
                          size="small"
                          label="Username"
                          className={`autofill_inputlog ${isDark ? 'darkmd' : 'lightmd'}`}
                          variant="outlined"
                          error={!!errors.UserName}
                          {...register('UserName')}
                          onInput={(e) => {
                            e.target.value = e.target.value.trim();
                          }}
                          fullWidth
                          InputLabelProps={{
                            sx: {
                              color: '#2c2c2c',
                            },
                          }}
                          inputProps={{
                            style: {
                              color: '#2c2c2c',
                            },
                            placeholder: 'Enter your username',
                          }}
                          sx={{
                            mb: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: '#fff',
                              '& fieldset': {
                                borderColor: '#e2e8f0',
                              },
                              '&:hover fieldset': {
                                borderColor: '#e2e8f0',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#e2e8f0',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: '#2c2c2c',
                              '&.Mui-focused': {
                                color: '#1976d2',
                              },
                            },
                            '& .MuiOutlinedInput-input': {
                              color: '#334155',
                            },
                          }}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          className={`autofill_inputlog ${isDark ? 'darkmd' : 'lightmd'}`}
                          label="Password"
                          type={showPassword ? 'text' : 'password'}
                          InputLabelProps={{
                            sx: {
                              color: '#2c2c2c',
                            },
                          }}
                          inputProps={{
                            style: {
                              color: '#2c2c2c',
                            },
                            placeholder: 'Enter your password',
                          }}
                          sx={{
                            mb: 1,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '8px',
                              backgroundColor: '#fff',
                              '& fieldset': {
                                borderColor: '#e2e8f0',
                              },
                              '&:hover fieldset': {
                                borderColor: '#e2e8f0',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#e2e8f0',
                              },
                            },
                            '& .MuiInputLabel-root': {
                              color: '#2c2c2c',
                              '&.Mui-focused': {
                                color: '#1976d2',
                              },
                            },
                            '& .MuiOutlinedInput-input': {
                              color: '#334155',
                            },
                          }}
                          error={!!errors.Password}
                          {...register('Password')}
                          onInput={(e) => {
                            e.target.value = e.target.value.trim();
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={handleTogglePassword}
                                  sx={{
                                    backgroundColor: 'transparent',
                                    color: '#2c2c2c',
                                    '&:hover': {
                                      backgroundColor: 'transparent',
                                    },
                                  }}
                                >
                                  {showPassword ? <i class="fa-sharp fa-regular fa-eye-slash" style={{ fontSize: "16px" }}></i> : <i class="fa-sharp fa-regular fa-eye" style={{ fontSize: "16px" }}></i>}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                          }}
                        >
                          <FormControlLabel
                            px={3}
                            sx={{
                              margin: 0,
                              '& .MuiFormControlLabel-label': {
                                fontSize: '14px',
                              },
                            }}
                            control={
                              <Checkbox
                                sx={{
                                  margin: 0,
                                  padding: 0,
                                  color: theme.palette.mode === 'light' ? '#d0d0d0' : '#EBF2F5',
                                  '&.Mui-checked': {
                                    color: theme.palette.background.bgpurple,
                                  },
                                  '&.MuiCheckbox-indeterminate': {
                                    color: theme.palette.background.bgpurple,
                                  },
                                }}
                                checked={rememberMe}
                                onChange={e => setRememberMe(e.target.checked)}
                              />
                            }
                            label="Remember me"
                          />
                          {/* <FormControlLabel color={theme.palette.background.bgpurple} control={<Checkbox
                                                    checked={rememberMe}
                                                    onChange={(e) => setRememberMe(e.target.checked)}
                                                    variant='secondary'
                                                />} label="Remember me" /> */}
                          <Typography
                            sx={{
                              cursor: 'pointer',
                            }}
                            onClick={handleNavigate}
                            color={theme.palette.background.bgpurple}
                            variant="body2"
                          >
                            Forgot Password?
                          </Typography>
                        </Box>
                        <Box className="modal-btn-voilet">
                          {/* <Button
                          variant="contained"
                          fullWidth
                          bgColor={theme.palette.background.bgpurple}
                          textColor={theme.palette.custom.whitetext}
                          padding="12px 10px"
                          fontSize="16px"
                          disabled={isSubmitting}
                          type="submit"
                          sx={{ width: '100%', fontWeight: 600 }}
                        >
                          {isSubmitting ? 'Logging in...' : 'Log In'}
                        </Button> */}
                          <Button
                            variant="contained"
                            fullWidth
                            bgColor={theme.palette.background.bgpurple}
                            textColor={theme.palette.custom.whitetext}
                            padding="12px 10px"
                            fontSize="16px"
                            disabled={isSubmitting}
                            type="submit"
                            sx={{ width: '100%', fontWeight: 600 }}
                          >
                            {isSubmitting ? (
                              <div class="loaderbtn"></div>
                            ) : (
                              "Log In"
                            )}
                          </Button>
                        </Box>
                        
                        <Divider
                          sx={{
                            mt: { xs: 2, sm: 2, md: 2, lg: 1, xl: 2 },
                            borderColor: '#adadad !important',
                            '& .MuiDivider-wrapper': {
                              fontSize: '13px',
                              fontWeight: 500,
                              color: '#adadad',
                            },
                            '--Divider-childPosition': `${position}%`,
                          }}
                        >

                      </Divider>
                      <Box
                        pt={1}
                        textAlign={'center'}
                        color={'#adadad'}
                        fontSize={13}
                        fontWeight={400}
                      >
                        <Link sx={{ cursor: 'pointer' }} style={{ color: '#adadad' }} to="/privacy-notice" target="_blank"
                          rel="noopener noreferrer">
                          Privacy Notice
                        </Link>{' '}
                        |{' '}
                        <Link sx={{ cursor: 'pointer' }} style={{ color: '#adadad' }} to="/terms" target="_blank"
                          rel="noopener noreferrer">
                          Term of service
                        </Link>
                      </Box>
                     
                    </Box>
                  </form>
                </Box>
                    </>
                  )}
              </Box>
            </Box>
          </Box>
          <Box
            className=" portal-card log-width"
            width={{ xs: '100%', sm: 500, md: 300, lg: 500, xl: 550 }}
            display={{xs: 'none', sm: 'none',md:'none',lg:'flex',xl:'flex'}}
            alignItems="center"
          >
          <Box 
          sx={{
        // py:{ xs: 3, sm: 3, md: 3, lg: 6, xl: 6 },                
        // minHeight: "100%",   
        m: "auto",          
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%"
      }}>
          <FeatureSlider />
          {/* <LoopComponent /> */}
          </Box>
          </Box>
          </Box>
          {/* <AnnouncementsLoginCard /> */}
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
